import React from 'react';
//interstate
import { Button } from '@interstate/components/Button';
import { TextInput } from '@interstate/components/TextInput';

import {
    Container,
    LeftSection,
    RightSection,
    Title,
    Form,
    InputGroup,
    InputContainer,
    Label,
    GuideTitle,
    GuideSection,
    GuideSubTitle,
    GuideList,
    GuideListItem
} from './RoleAssumption.style';
import { useRoleAssumptionHook } from '../../hooks';
import { DEALER_USERNAME_PLACEHOLDER, INVALID_ROLE_ASSUMPTION_USER_ERROR, USERNAME_MAX_LENGTH_ERROR } from '../../constant';
import { ApiRequestStatus } from '@makemydeal/dr-shared-types';

export const RoleAssumption: React.FC<any> = () => {
    const { username, isValidRoleAssumptionUser, requestStatus, handleSubmit, handleInputChange } = useRoleAssumptionHook();

    return (
        <Container>
            <LeftSection>
                <Title>VIEW AS USER</Title>
                <Form onSubmit={handleSubmit}>
                    <InputGroup>
                        <Label htmlFor="username">Username</Label>
                        <InputContainer>
                            <TextInput
                                name="username-input"
                                id="username"
                                value={username}
                                onChange={handleInputChange}
                                placeholder={DEALER_USERNAME_PLACEHOLDER}
                                data-testid="username-input"
                                maxLength={50}
                                minLength={2}
                                required="requiredNoAnnotation"
                                displayLabel={false}
                                disabled={requestStatus === ApiRequestStatus.Loading}
                                hasError={!isValidRoleAssumptionUser}
                                errorMessage={!isValidRoleAssumptionUser ? INVALID_ROLE_ASSUMPTION_USER_ERROR : ''}
                                customErrorMessages={{
                                    exceedsMaxLength: USERNAME_MAX_LENGTH_ERROR
                                }}
                            />
                            <Button
                                type="submit"
                                buttonStyle="primary"
                                disabled={!username.trim() || username.length > 50}
                                data-testid="submit-button"
                                size="medium"
                                isLoading={requestStatus === ApiRequestStatus.Loading}
                            >
                                Submit
                            </Button>
                        </InputContainer>
                    </InputGroup>
                </Form>
            </LeftSection>

            <RightSection>
                <GuideTitle>User Guide (helpful tips):</GuideTitle>

                <GuideSection>
                    <GuideSubTitle>Important Reminders</GuideSubTitle>
                    <GuideList>
                        <GuideListItem>Some pages may not be real-only; actions taken could impact real dealer data.</GuideListItem>
                        <GuideListItem>
                            Employees are no longer viewing as when redirected to an application outside of CRM (e.g., clicking Open
                            Deal or similar).
                        </GuideListItem>
                        <GuideListItem>Do not leave the View As experience running.</GuideListItem>
                    </GuideList>
                </GuideSection>

                <GuideSection>
                    <GuideSubTitle>Access</GuideSubTitle>
                    <GuideList>
                        <GuideListItem>Only employees can view as a Dealer user.</GuideListItem>
                        <GuideListItem>Employees must have sufficient access and a Bridge ID.</GuideListItem>
                        <GuideListItem>Employees must have a Bridge ID linked to the CRM application.</GuideListItem>
                        <GuideListItem>Employees cannot view as other employees.</GuideListItem>
                    </GuideList>
                </GuideSection>

                <GuideSection>
                    <GuideSubTitle>View as Dealer user</GuideSubTitle>
                    <GuideList>
                        <GuideListItem>
                            Employees must use the impersonating dealer's Vin username. The Bridge username will generate an error
                            unless it is the same as the username.
                        </GuideListItem>
                        <GuideListItem>
                            If the dealer username and Bridge username are the same, either can be used. Finder use employees must
                            use the impersonating dealer's username.
                        </GuideListItem>
                        <GuideListItem>Employees can only view as a single Dealer user per session.</GuideListItem>
                        <GuideListItem>During a view as session the user will need the same as the session.</GuideListItem>
                        <GuideListItem>User access is limited to what the dealer sees and permissions.</GuideListItem>
                        <GuideListItem>
                            Employee can view reports from the same browser and see the current view as session.
                        </GuideListItem>
                    </GuideList>
                </GuideSection>

                <GuideSection>
                    <GuideSubTitle>Ending A Session</GuideSubTitle>
                    <GuideList>
                        <GuideListItem>Logging out of CRM and using the same as session.</GuideListItem>
                        <GuideListItem>
                            Closing the browser may end the session (determined by the users browser settings).
                        </GuideListItem>
                        <GuideListItem>When a view as session ends, all open windows/tabs in view as will stop.</GuideListItem>
                    </GuideList>
                </GuideSection>
            </RightSection>
        </Container>
    );
};

export default RoleAssumption;
